import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  addAlpacaLeadRole,
  getCurrentAlpacaLead,
  getPendingTeacherBySchoolId,
  getTeachersBySchoolId,
  removeAlpacaLeadRole,
  resendTeacherInvite,
  updateTeacherProfileWithEmail
} from "../services/APIClient";
import "./TeacherAssignAlpacaLeadModal.css";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalisePrefill = (prefill) => ({
  teacherName: String(prefill?.teacherName || prefill?.teacher_name || "").trim(),
  email: String(prefill?.email || prefill?.teacherEmail || prefill?.teacher_email || "").trim(),
  mobileNumber: String(prefill?.mobileNumber || prefill?.mobile_number || prefill?.phoneNumber || "").trim()
});

const firstPresentValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const isFalseValue = (value) =>
  value === false || ["false", "0", "no"].includes(String(value || "").trim().toLowerCase());

const setupIncompleteStatuses = new Set([
  "pending",
  "invited",
  "invite sent",
  "invitation sent",
  "email not opened",
  "not set up",
  "not setup",
  "setup incomplete",
  "setup not complete",
  "awaiting setup"
]);

const getRoleLabel = (teacher) => {
  const additionalRole = Array.isArray(teacher?.additionalRoles) ? teacher.additionalRoles[0] : "";
  return teacher?.roleLabel || teacher?.role || additionalRole || "Teacher";
};

const getAccountStatus = (teacher, fallbackStatus) => {
  const status = firstPresentValue(
    teacher?.accountStatus,
    teacher?.account_status,
    teacher?.userStatus,
    teacher?.user_status,
    teacher?.status,
    teacher?.inviteStatus,
    teacher?.invite_status,
    fallbackStatus
  );

  return status ? String(status).trim() : "Active account";
};

const normaliseTeacher = (teacher, fallbackStatus) => {
  const teacherData = teacher && typeof teacher === "object" ? teacher : {};
  const name =
    teacherData.name ||
    teacherData.fullName ||
    teacherData.displayName ||
    [teacherData.firstName, teacherData.lastName].filter(Boolean).join(" ") ||
    "Unnamed teacher";

  return {
    ...teacherData,
    id:
      teacherData.id ||
      teacherData._id ||
      teacherData.teacherid ||
      teacherData.teacherId ||
      teacherData.teacher_id ||
      teacherData.email,
    name,
    email: teacherData.email || teacherData.teacherEmail || "",
    mobileNumber:
      teacherData.mobileNumber ||
      teacherData.mobile_number ||
      teacherData.phoneNumber ||
      teacherData.phone_number ||
      teacherData.phone ||
      "",
    role: getRoleLabel(teacherData),
    accountStatus: getAccountStatus(teacherData, fallbackStatus)
  };
};

const needsAccountSetup = (teacher) => {
  const status = normaliseText(teacher?.accountStatus);

  return (
    setupIncompleteStatuses.has(status) ||
    isFalseValue(teacher?.setupComplete) ||
    isFalseValue(teacher?.hasCompletedSetup) ||
    isFalseValue(teacher?.accountSetupComplete)
  );
};

const renderStatusLabel = (teacher) =>
  needsAccountSetup(teacher) ? "Setup not complete" : teacher?.accountStatus || "Active account";

const mergeTeachers = (teachers) => {
  const teachersByKey = new Map();

  teachers.forEach((teacher) => {
    const key = normaliseText(teacher.email || teacher.id);
    if (!key) {
      return;
    }

    const existingTeacher = teachersByKey.get(key);
    if (!existingTeacher || needsAccountSetup(existingTeacher)) {
      teachersByKey.set(key, teacher);
    }
  });

  return Array.from(teachersByKey.values()).sort((firstTeacher, secondTeacher) =>
    firstTeacher.name.localeCompare(secondTeacher.name)
  );
};

const findSingleExactMatch = (teachers, prefillContext) => {
  const teacherName = normaliseText(prefillContext.teacherName);
  const email = normaliseText(prefillContext.email);

  if (!teacherName && !email) {
    return null;
  }

  const matches = teachers.filter((teacher) => {
    const nameMatches = teacherName && normaliseText(teacher.name) === teacherName;
    const emailMatches = email && normaliseText(teacher.email) === email;

    return nameMatches || emailMatches;
  });

  return matches.length === 1 ? matches[0] : null;
};

function TeacherAssignAlpacaLeadModal({
  show,
  handleClose,
  schoolid,
  currentTeacherId = null,
  currentTeacherName,
  prefill = null,
  onAssignLead = null,
  onSendInviteAndRequestLead = null,
  onSendWhatsAppInvite = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("choose");
  const [leadChoice, setLeadChoice] = useState("self");
  const [teachers, setTeachers] = useState([]);
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [leadMobileNumber, setLeadMobileNumber] = useState("");
  const [completedTeacher, setCompletedTeacher] = useState(null);
  const [completedAction, setCompletedAction] = useState("assigned");
  const [currentLead, setCurrentLead] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const currentTeacher = useMemo(() => {
    const matchedById = currentTeacherId
      ? teachers.find(
          (teacher) => String(teacher.id) === String(currentTeacherId) && !needsAccountSetup(teacher)
        )
      : null;

    if (matchedById) return matchedById;

    const matchedByName = teachers.find(
      (teacher) =>
        currentTeacherName &&
        normaliseText(teacher.name) === normaliseText(currentTeacherName) &&
        !needsAccountSetup(teacher)
    );

    return matchedByName || {
      id: currentTeacherId || "current-teacher",
      name: currentTeacherName || "Me",
      email: "",
      mobileNumber: "",
      role: "Teacher",
      accountStatus: "Active account"
    };
  }, [currentTeacherId, currentTeacherName, teachers]);

  const filteredTeachers = useMemo(() => {
    const query = normaliseText(teacherSearchTerm);

    if (!query) {
      return [];
    }

    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.role, teacher.accountStatus].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [teacherSearchTerm, teachers]);

  const chosenTeacher = leadChoice === "self" ? currentTeacher : selectedTeacher;
  const chosenTeacherId = chosenTeacher?.id;
  const chosenTeacherMobileNumber = chosenTeacher?.mobileNumber;
  const chosenTeacherNeedsSetup = Boolean(chosenTeacher && needsAccountSetup(chosenTeacher));
  const trimmedLeadMobileNumber = String(leadMobileNumber || "").trim();
  const canAssignLead = Boolean(chosenTeacher && !isSubmitting);
  const canSendInviteAndRequestLead = Boolean(chosenTeacher?.email && !isSubmitting);

  useEffect(() => {
    if (!show) {
      return;
    }

    const prefillMatchesCurrentTeacher =
      prefillContext.teacherName &&
      currentTeacherName &&
      normaliseText(prefillContext.teacherName) === normaliseText(currentTeacherName);
    const hasTeacherPrefill = Boolean(prefillContext.teacherName || prefillContext.email);

    setStage("choose");
    setLeadChoice(hasTeacherPrefill && !prefillMatchesCurrentTeacher ? "other" : "self");
    setTeachers([]);
    setCurrentLead(null);
    setTeacherSearchTerm(prefillContext.teacherName || prefillContext.email);
    setSelectedTeacher(null);
    setLeadMobileNumber(prefillContext.mobileNumber);
    setCompletedTeacher(null);
    setCompletedAction("assigned");
    setError("");

    if (!schoolid) {
      setError("Teachers could not be loaded. You can still assign yourself.");
      return;
    }

    setIsLoading(true);
    Promise.allSettled([
      getTeachersBySchoolId(schoolid),
      getPendingTeacherBySchoolId(schoolid),
      getCurrentAlpacaLead(schoolid)
    ])
      .then(([teachersResult, pendingTeachersResult, currentLeadResult]) => {
        const activeTeachers =
          teachersResult.status === "fulfilled" && Array.isArray(teachersResult.value)
            ? teachersResult.value
            : [];
        const pendingTeachers =
          pendingTeachersResult.status === "fulfilled" && Array.isArray(pendingTeachersResult.value)
            ? pendingTeachersResult.value
            : [];
        if (currentLeadResult.status === "fulfilled" && currentLeadResult.value && !currentLeadResult.value.error) {
          const normalisedLead = normaliseTeacher(currentLeadResult.value);
          if (normalisedLead.name && normalisedLead.name !== "Unnamed teacher") {
            setCurrentLead(normalisedLead);
          }
        }
        const normalisedTeachers = mergeTeachers([
          ...activeTeachers.map((teacher) => normaliseTeacher(teacher, "Active account")),
          ...pendingTeachers.map((teacher) => normaliseTeacher(teacher, "Pending"))
        ])
          .filter((teacher) => teacher.id && teacher.active !== false);
        const prefilledTeacher = findSingleExactMatch(normalisedTeachers, prefillContext);

        setTeachers(normalisedTeachers);

        if (prefilledTeacher) {
          const prefilledCurrentTeacher =
            currentTeacherName &&
            normaliseText(prefilledTeacher.name) === normaliseText(currentTeacherName) &&
            !needsAccountSetup(prefilledTeacher);

          setLeadChoice(prefilledCurrentTeacher ? "self" : "other");
          setSelectedTeacher(prefilledCurrentTeacher ? null : prefilledTeacher);
          setLeadMobileNumber(prefilledTeacher.mobileNumber || prefillContext.mobileNumber);
        }

        if (teachersResult.status === "rejected" && pendingTeachersResult.status === "rejected") {
          setError("Teachers could not be loaded. You can still assign yourself.");
        }
      })
      .catch((loadError) => {
        console.error("Error loading teachers for ALPACA Lead workflow:", loadError);
        setError("Teachers could not be loaded. You can still assign yourself.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    show,
    schoolid,
    currentTeacherId,
    currentTeacherName,
    prefillContext
  ]);

  useEffect(() => {
    if (!chosenTeacherId) {
      setLeadMobileNumber(prefillContext.mobileNumber);
      return;
    }

    setLeadMobileNumber(chosenTeacherMobileNumber || prefillContext.mobileNumber);
  }, [chosenTeacherId, chosenTeacherMobileNumber, prefillContext.mobileNumber]);

  const handleSelectTeacher = (teacher) => {
    setSelectedTeacher(teacher);
    setLeadMobileNumber(teacher.mobileNumber || "");
    setError("");
  };

  const handleAssignLead = async () => {
    if (!canAssignLead) {
      return;
    }

    if (chosenTeacherNeedsSetup) {
      setStage("setup");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const mobileNumberChanged =
        normaliseText(trimmedLeadMobileNumber) !== normaliseText(chosenTeacher.mobileNumber);
      const chosenTeacherWithMobile = {
        ...chosenTeacher,
        mobileNumber: trimmedLeadMobileNumber || chosenTeacher.mobileNumber || ""
      };
      const payload = {
        schoolid,
        teacherId: chosenTeacher.id,
        teacher: chosenTeacherWithMobile,
        assignmentType: leadChoice,
        mobileNumber: trimmedLeadMobileNumber
      };

      if (mobileNumberChanged && schoolid && chosenTeacher.id !== "current-teacher") {
        await updateTeacherProfileWithEmail(schoolid, chosenTeacher.id, {
          mobileNumber: trimmedLeadMobileNumber
        });
      }

      if (onAssignLead) {
        await onAssignLead(payload);
      } else {
        await addAlpacaLeadRole(schoolid, chosenTeacher.id);
        if (currentLead?.id && currentLead.id !== chosenTeacher.id) {
          await removeAlpacaLeadRole(schoolid, currentLead.id);
        }
      }

      setCompletedTeacher(chosenTeacherWithMobile);
      setCompletedAction("assigned");
      setStage("success");
    } catch (submitError) {
      console.error("Error assigning ALPACA Lead:", submitError);
      setError("The ALPACA Lead could not be assigned. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendInviteAndRequestLead = async () => {
    if (!canSendInviteAndRequestLead) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const payload = {
        schoolid,
        teacherId: chosenTeacher.id,
        teacher: chosenTeacher,
        requestType: "account-setup-and-lead"
      };

      if (onSendInviteAndRequestLead) {
        await onSendInviteAndRequestLead(payload);
      } else {
        await resendTeacherInvite(chosenTeacher.id);
      }

      setCompletedTeacher(chosenTeacher);
      setCompletedAction("requested");
      setStage("success");
    } catch (submitError) {
      console.error("Error sending ALPACA Lead invite request:", submitError);
      setError("The invite and lead request could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherLead = () => {
    setStage("choose");
    setLeadChoice("self");
    setTeacherSearchTerm("");
    setSelectedTeacher(null);
    setLeadMobileNumber("");
    setCompletedTeacher(null);
    setCompletedAction("assigned");
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: completedTeacher?.email || completedTeacher?.name || "",
        message: "ALPACA Lead assignment complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderChoiceOption = (choice, title, copy, detail) => {
    const selected = leadChoice === choice;

    return (
      <button
        type="button"
        className={`assign-alpaca-lead-option${selected ? " selected" : ""}`}
        onClick={() => {
          setLeadChoice(choice);
          setError("");
        }}
        aria-pressed={selected}
      >
        <span className="assign-alpaca-lead-radio" aria-hidden="true" />
        <span className="assign-alpaca-lead-option-copy">
          <strong>{title}</strong>
          <span>{copy}</span>
          {detail && <span className="assign-alpaca-lead-pill">{detail}</span>}
        </span>
      </button>
    );
  };

  const renderTeacherResults = () => {
    if (isLoading) {
      return (
        <div className="assign-alpaca-lead-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading teachers...</span>
        </div>
      );
    }

    if (!teacherSearchTerm.trim()) {
      return <p className="assign-alpaca-lead-muted">Start with the teacher's name or email.</p>;
    }

    if (filteredTeachers.length < 1) {
      return (
        <div className="assign-alpaca-lead-empty">
          <strong>No matching teachers found.</strong>
          <span>Try another teacher name or email.</span>
        </div>
      );
    }

    return filteredTeachers.map((teacher) => {
      const selected = selectedTeacher?.id === teacher.id;
      const setupRequired = needsAccountSetup(teacher);

      return (
        <button
          key={teacher.id}
          type="button"
          className={`assign-alpaca-lead-result${selected ? " selected" : ""}${setupRequired ? " setup-required" : ""}`}
          onClick={() => handleSelectTeacher(teacher)}
          aria-label={`Select ${teacher.name}`}
          aria-pressed={selected}
        >
          <span className="assign-alpaca-lead-radio" aria-hidden="true" />
          <span className="assign-alpaca-lead-result-copy">
            <strong>{teacher.name}</strong>
            <span>{teacher.email || "No email shown"}</span>
            <span className={`assign-alpaca-lead-pill${setupRequired ? " warning" : ""}`}>
              {renderStatusLabel(teacher)}
            </span>
          </span>
          {selected && <span className="assign-alpaca-lead-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderSelectedSummary = () => {
    if (!chosenTeacher) {
      return null;
    }

    return (
      <div className="assign-alpaca-lead-summary">
        <strong>Selected lead</strong>
        <span className="assign-alpaca-lead-row">
          <span>Teacher</span>
          <span>{chosenTeacher.name}</span>
        </span>
        <span className="assign-alpaca-lead-row">
          <span>Email</span>
          <span>{chosenTeacher.email || "No email shown"}</span>
        </span>
        <span className="assign-alpaca-lead-row">
          <span>Status</span>
          <span>{renderStatusLabel(chosenTeacher)}</span>
        </span>
        <span className="assign-alpaca-lead-row">
          <span>Mobile</span>
          <span>{trimmedLeadMobileNumber || "Not provided"}</span>
        </span>
      </div>
    );
  };

  const renderChooseLead = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Assign ALPACA Lead</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="assign-alpaca-lead-intro">
          Choose who should be the ALPACA Lead for this school.
        </p>
        {currentLead && (
          <div className="assign-alpaca-lead-current-lead">
            <strong>Current ALPACA Lead:</strong>
            <span>{currentLead.name}</span>
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="assign-alpaca-lead-options" aria-label="ALPACA Lead options">
          {renderChoiceOption(
            "self",
            "I will be the ALPACA Lead",
            `${currentTeacher.name} will be the named lead for this school.`,
            "Current teacher"
          )}
          {renderChoiceOption(
            "other",
            "Choose another teacher",
            "Find a teacher from this school and assign them as the lead.",
            selectedTeacher ? "Teacher selected" : "Search teacher"
          )}
        </div>

        {leadChoice === "other" && (
          <div className="assign-alpaca-lead-section">
            <Form.Group controlId="assignAlpacaLeadTeacherSearch">
              <Form.Label>Which teacher should be the ALPACA Lead?</Form.Label>
              <Form.Text as="div" className="assign-alpaca-lead-field-label">
                Search teacher
              </Form.Text>
              <Form.Control
                type="search"
                value={teacherSearchTerm}
                onChange={(event) => {
                  setTeacherSearchTerm(event.target.value);
                  setSelectedTeacher(null);
                  setError("");
                }}
                aria-label="Search teacher for ALPACA Lead"
              />
              <Form.Text className="text-muted">
                Choose from teachers in this school.
              </Form.Text>
            </Form.Group>
            <div className="assign-alpaca-lead-results" aria-label="Matching teachers">
              {renderTeacherResults()}
            </div>
          </div>
        )}

        {renderSelectedSummary()}

        {chosenTeacher && (
          <Form.Group className="assign-alpaca-lead-section" controlId="assignAlpacaLeadMobileNumber">
            <Form.Label>Mobile number</Form.Label>
            <Form.Text as="div" className="assign-alpaca-lead-field-label">
              Optional contact number
            </Form.Text>
            <Form.Control
              type="tel"
              value={leadMobileNumber}
              onChange={(event) => {
                setLeadMobileNumber(event.target.value);
                setError("");
              }}
              placeholder="Mobile number"
              aria-label="ALPACA Lead mobile number"
            />
            <Form.Text className="text-muted">
              Used for ALPACA Lead contact details when provided.
            </Form.Text>
          </Form.Group>
        )}

        {chosenTeacher && (
          <div className="assign-alpaca-lead-note">
            Class access stays unchanged.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="assign-alpaca-lead-footer-note">
          {chosenTeacher ? `${chosenTeacher.name} selected.` : "Choose the teacher first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleAssignLead} disabled={!canAssignLead}>
          {isSubmitting ? "Assigning..." : "Assign lead"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderAccountSetupNeeded = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Account Setup Needed First</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="assign-alpaca-lead-intro">
          {chosenTeacher?.name} needs to finish account setup before they can be the active lead.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="assign-alpaca-lead-warning">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>Teacher details</strong>
            <span>{chosenTeacher?.name}</span>
            <span>{chosenTeacher?.email || "No email shown"}</span>
          </span>
        </Alert>
        {renderSelectedSummary()}
      </Modal.Body>
      <Modal.Footer>
        <span className="assign-alpaca-lead-footer-note">
          {chosenTeacher?.email ? "Setup invite needed." : "An email is needed first."}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("choose")}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleSendInviteAndRequestLead}
          disabled={!canSendInviteAndRequestLead}
        >
          {isSubmitting ? "Sending..." : "Send invite and request lead"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => {
    const requestSent = completedAction === "requested";

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">{requestSent ? "Lead Request Sent" : "Lead Updated"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success" className="assign-alpaca-lead-success">
            <FaCheckCircle aria-hidden="true" />
            <span>
              <strong>
                {requestSent
                  ? `An invite and lead request have been sent for ${completedTeacher?.name}.`
                  : `${completedTeacher?.name} is now the ALPACA Lead.`}
              </strong>
              <span>Teacher details and class access stayed unchanged.</span>
            </span>
          </Alert>
          <div className="assign-alpaca-lead-next">
            <strong>What happens next</strong>
            <span>
              {requestSent
                ? `${completedTeacher?.name} can finish account setup from the invite email.`
                : `${completedTeacher?.name} is the named ALPACA Lead for this school.`}
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span className="assign-alpaca-lead-footer-note">
            {embedded ? "Done returns to SENSAI." : requestSent ? "Request sent." : "Lead updated."}
          </span>
          <Button variant="outline-secondary" onClick={resetForAnotherLead}>
            Change lead again
          </Button>
          {!requestSent && onSendWhatsAppInvite && (
            <Button
              variant="outline-secondary"
              onClick={() => onSendWhatsAppInvite({
                mobileNumber: completedTeacher?.mobileNumber
              })}
            >
              Send WhatsApp invite
            </Button>
          )}
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        </Modal.Footer>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-assign-alpaca-lead-modal"
    >
      {stage === "success"
        ? renderSuccess()
        : stage === "setup"
          ? renderAccountSetupNeeded()
          : renderChooseLead()}
    </Modal>
  );
}

export default TeacherAssignAlpacaLeadModal;
